/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo } from "react";

//internal-deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { useFlowThemeOverride } from "@swiggy-private/checkout-core";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";

//constants
import {
    PAY_TEXT,
    PAY_INSTANTLY_SUBTEXT_TURBO_WIDGET,
    PAY_USING_ACCESSIBILITY_LABEL,
    SLIDE_TO_PAY_AMOUNT,
} from "@payment-ui/constants/TurboPayment";
import {
    resolveTurboPayButtonThemeFlow,
    TURBO_PAY_BUTTON_THEME_OVERRIDES,
} from "@payment-ui/constants/TurboPayButtonTheme";

//styles
import { BoltText, BoltWrapper, DiscountAmount, PayButton } from "./styles";

//helpers
import { format } from "@swiggy-private/js-utils";

interface TurboV2PaymentButtonProps {
    finalAmount: string;
    discountAmount?: string;
    showBolt?: boolean;
    onPayClick: VoidFunction;
    dataTestId?: string;
    isPop?: boolean;
    paymentMethodName?: string;
}

function TurboV2PaymentButton({
    finalAmount,
    onPayClick,
    discountAmount,
    showBolt,
    dataTestId,
    isPop,
    paymentMethodName,
}: TurboV2PaymentButtonProps) {
    // Screen readers announce the method along with the amount, e.g. "Pay ₹183 using Credit card".
    // Falls back to just the amount when the method name is unavailable.
    const accessibilityLabel = paymentMethodName
        ? format(PAY_USING_ACCESSIBILITY_LABEL, format(SLIDE_TO_PAY_AMOUNT, finalAmount), paymentMethodName)
        : `${PAY_TEXT} ${format(SLIDE_TO_PAY_AMOUNT, finalAmount)}`;

    const flow = resolveTurboPayButtonThemeFlow({ isQuickCheckoutEnabled: false, isPop });
    const { customTheme, cssVariables, baseTheme } = useFlowThemeOverride({
        flow,
        overridesByFlow: TURBO_PAY_BUTTON_THEME_OVERRIDES,
    });

    return (
        <ThemeProvider theme={customTheme} colorMode={baseTheme.colorMode} design_language={baseTheme.designLanguage}>
            <div style={cssVariables}>
                <PayButton
                    variant="positive"
                    onClick={onPayClick}
                    data-testid={dataTestId}
                    isPop={isPop}
                    aria-label={accessibilityLabel}
                >
                    <Text
                        data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_TEXT}
                        variant="V2.body1.neutral.bold"
                        color="text_Button_Primary"
                    >
                        {PAY_TEXT}{" "}
                        {discountAmount && discountAmount !== finalAmount && (
                            <Text
                                data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_DISCOUNT}
                                variant="V2.overline.neutral.medium"
                                color="text_inverse_Med_Emphasis"
                                as="span"
                                aria-hidden="true"
                            >
                                <DiscountAmount>{format(SLIDE_TO_PAY_AMOUNT, discountAmount)}</DiscountAmount>
                            </Text>
                        )}
                        {format(SLIDE_TO_PAY_AMOUNT, finalAmount)}
                    </Text>
                    {showBolt && (
                        <BoltWrapper data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_BOLT}>
                            <SpriteIconWeb iconName="boltFilled12" fillColor="text_inverse_Med_Emphasis" />
                            <BoltText
                                data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_BOLT_TEXT}
                                variant="V2.overline.neutral.bold_spaced"
                            >
                                {PAY_INSTANTLY_SUBTEXT_TURBO_WIDGET}
                            </BoltText>
                        </BoltWrapper>
                    )}
                </PayButton>
            </div>
        </ThemeProvider>
    );
}

export default memo(TurboV2PaymentButton);
